/**
 * Renderer CSS class list. Supported formats are preferred space-separated class names
 * (for example, "class1 class2 class3") and accepted dot-separated class names
 * (for example, ".class1.class2.class3"). This is not a general CSS selector;
 * new code should prefer the space-separated format. Concrete renderers can use
 * _normalizeCss() to normalize accepted input to the preferred space-separated format.
 * @typedef {string} RendererCss
 */

/**
 * @typedef {object} RendererCollectionItem
 * @property {*|array|null} [content] Item content. When omitted, label is used as item content.
 * @property {string|*|null} [label] Item label/content. A string is interpreted as text; any other value is treated as already rendered content.
 * @property {RendererCss|null} [css] Item CSS.
 * @property {object|null} [props] Item props.
 * @property {boolean} [disabled] Disables the item when supported by a concrete renderer.
 */

/**
 * Collection rendering source precedence: explicit content first, then onItemCount()/onItemRender()
 * callbacks, then items.
 * @typedef {object} RendererCollectionOptions
 * @property {RendererCss|null} [css] Collection CSS.
 * @property {object|null} [props] Collection props.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererCollectionItem>|null} [items] Items rendered when explicit content and callback rendering are not provided.
 */

/**
 * @typedef {object} RendererNavigationItemOptions
 * @property {string|*|null} [label] Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
 * @property {string|*|null} [icon] Optional icon. A string is interpreted as an icon name; any other value is treated as an already rendered icon node.
 * @property {RendererCss|null} [css] Item CSS.
 * @property {object|null} [props] Item props.
 * @property {string|null} [url] Navigation/menu target URL.
 * @property {boolean} [disabled] Disables the item.
 * @property {function(Event): void} [onClick] Click handler.
 * @property {array<RendererNavigationItemOptions>} [items] Raw child navigation/menu item data.
 */

/**
 * Navigation rendering source precedence: explicit content first, then onItemCount()/onItemRender()
 * callbacks, then raw options.items.
 * @typedef {object} RendererNavOptions
 * @property {RendererCss|null} [css] Navigation CSS.
 * @property {object|null} [props] Navigation props.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererNavigationItemOptions>|null} [items] Raw items rendered when explicit content and callback rendering are not provided.
 */

/**
 * Menu rendering source precedence: explicit content first, then onItemCount()/onItemRender()
 * callbacks, then raw options.items.
 * @typedef {object} RendererMenuOptions
 * @property {RendererCss|null} [css] Menu CSS.
 * @property {object|null} [props] Menu props.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererNavigationItemOptions>|null} [items] Raw items rendered when explicit content and callback rendering are not provided.
 */

/**
 * Breadcrumbs rendering source precedence: explicit content first, then onItemCount()/onItemRender()
 * callbacks, then raw options.items.
 * @typedef {object} RendererBreadcrumbsOptions
 * @property {RendererCss|null} [css] Breadcrumbs CSS.
 * @property {object|null} [props] Breadcrumbs props.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererNavigationItemOptions>|null} [items] Raw breadcrumb items rendered when explicit content and callback rendering are not provided.
 */

/**
 * @typedef {object} RendererLinkOptions
 * @property {string|null} [url] Link target URL.
 * @property {function(): string|null} [onUrlGet] Returns link target URL lazily. When provided, it takes precedence over url.
 * @property {RendererCss|null} [css] Link CSS.
 * @property {object|null} [props] Link props.
 */

/**
 * @typedef {'bold'|'italic'|'underline'|'strikethrough'|'code'|'mark'|'small'|'sup'|'sub'|'deleted'|'inserted'} RendererTextStyleName
 */

/**
 * @typedef {object} RendererTextStyleOptions
 * @property {RendererCss|null} [css] Text style CSS.
 * @property {object|null} [props] Text style props.
 */

/**
 * @typedef {object} RendererHeadingOptions
 * @property {RendererCss|null} [css] Heading CSS.
 * @property {object|null} [props] Heading props.
 */

/**
 * @typedef {object} RendererIconOptions
 * @property {RendererCss|null} [css] Icon CSS.
 * @property {object|null} [props] Icon props/attributes.
 * @property {string|null} [style] Icon style/family, e.g. solid, regular, brands.
 * @property {string|null} [size] Icon size.
 * @property {boolean} [fixedWidth] Use fixed-width icon layout.
 * @property {boolean} [spin] Spin animation.
 * @property {boolean} [pulse] Pulse animation.
 * @property {number|string|null} [rotate] Icon rotation.
 * @property {string|null} [flip] Icon flip.
 */

/**
 * @typedef {object} RendererButtonOptions
 * @property {RendererCss|null} [css] Button CSS.
 * @property {object|null} [props] Button props.
 * @property {RendererIconOptions|null} [iconOptions] Nested icon rendering options.
 */

/**
 * @typedef {object} RendererLayoutOptions
 * @property {RendererCss|null} [css] Layout CSS.
 * @property {object|null} [props] Layout props.
 * @property {string|null} [position] Layout position hint.
 * @property {string|null} [size] Layout size hint.
 * @property {boolean} [visible] Set to false to suppress rendering when supported by a concrete renderer.
 */

/**
 * @typedef {object} RendererFieldOptions
 * @property {RendererCss|null} [css] Field wrapper CSS.
 * @property {object|null} [props] Field wrapper props.
 * @property {string|*|null} [label] Field label. A string is interpreted as text; any other value is treated as already rendered content.
 * @property {string|null} [controlId] Control id used to bind the field label to the rendered control. The field helper does not mutate already-rendered content; pass the same id to the control props when label binding is needed.
 * @property {string|*|null} [hint] Optional help/hint content rendered near the control.
 * @property {string|*|array|null} [error] Optional validation error content rendered near the control.
 * @property {boolean} [required] Marks the field as required.
 * @property {boolean} [disabled] Marks the field as disabled at wrapper/semantic level when supported.
 * @property {boolean} [readOnly] Marks the field as read-only at wrapper/semantic level when supported.
 * @property {boolean} [invalid] Explicit invalid state. When omitted, concrete renderers may infer invalid state from error.
 */

/**
 * @typedef {object} RendererTextBoxOptions
 * @property {RendererCss|null} [css] Editor CSS.
 * @property {object|null} [props] Editor props.
 * @property {boolean} [multiline] Renders a multiline text editor instead of a single-line text input.
 * @property {function(): *} [onValueGet] Returns the current value when the value should be pulled lazily.
 * @property {function(*): void} [onValueSet] Stores the parsed value after user text changes.
 * @property {function(*): string} [onFormatValue] Formats a value to text for rendering.
 * @property {function(string): *} [onParseText] Parses user text back to a value.
 */

/**
 * @typedef {object} RendererSliderOptions
 * @property {RendererCss|null} [css] Slider CSS.
 * @property {object|null} [props] Slider props.
 * @property {number|null} [min] Minimum slider value.
 * @property {number|null} [max] Maximum slider value.
 * @property {number|null} [step] Slider value step.
 * @property {function(): number} [onValueGet] Returns the current slider value when it should be pulled lazily.
 * @property {function(number, Event=): void} [onValueSet] Stores the slider value after user interaction.
 * @property {function(number): string} [onFormatValue] Formats a slider value for display when supported by a concrete renderer.
 */

/**
 * @typedef {object} RendererProgressOptions
 * @property {RendererCss|null} [css] Progress CSS.
 * @property {object|null} [props] Progress props.
 * @property {number|null} [min] Minimum progress value.
 * @property {number|null} [max] Maximum progress value.
 * @property {boolean} [indeterminate] Renders an indeterminate progress indicator when supported by a concrete renderer.
 * @property {string|*|null} [label] Optional progress label. A string is interpreted as text; any other value is treated as already rendered content.
 * @property {function(): number} [onValueGet] Returns the current progress value when it should be pulled lazily.
 * @property {function(number): string} [onFormatValue] Formats a progress value for display when supported by a concrete renderer.
 */

/**
 * @typedef {object} RendererCheckBoxOptions
 * @property {RendererCss|null} [css] Checkbox CSS.
 * @property {object|null} [props] Checkbox props.
 * @property {function(): boolean} [onValueGet] Returns the current checkbox value when it should be pulled lazily.
 * @property {function(boolean): void} [onValueSet] Stores the checkbox value after user interaction.
 */

/**
 * @typedef {object} RendererRadioGroupItem
 * @property {*} value Item value.
 * @property {string|*|null} [label] Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
 * @property {boolean} [disabled] Disables the item.
 */

/**
 * @typedef {object} RendererRadioGroupOptions
 * @property {RendererCss|null} [css] Group CSS.
 * @property {object|null} [props] Group props.
 * @property {function(): *} [onValueGet] Returns the current group value when it should be pulled lazily.
 * @property {function(*, RendererRadioGroupItem=, Event=): void} [onValueSet] Stores the group value after user interaction.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererRadioGroupItem>} [items] Items rendered when explicit content and callback rendering are not provided.
 * @property {*|null} [emptyValue] Value representing the empty choice.
 * @property {string|*|null} [emptyLabel] Label for the empty choice.
 * @property {boolean} [emptyNotDisplayed] Set to true to suppress rendering of the empty choice.
 */

/**
 * @typedef {object} RendererCheckGroupItem
 * @property {*} value Item value.
 * @property {string|*|null} [label] Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
 * @property {boolean} [disabled] Disables the item.
 */

/**
 * @typedef {object} RendererCheckGroupOptions
 * @property {RendererCss|null} [css] Group CSS.
 * @property {object|null} [props] Group props.
 * @property {function(): array<*>} [onValueGet] Returns the current selected values when they should be pulled lazily.
 * @property {function(array<*>, RendererCheckGroupItem=, Event=): void} [onValueSet] Stores selected values after user interaction.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererCheckGroupItem>} [items] Items rendered when explicit content and callback rendering are not provided.
 */

/**
 * @typedef {object} RendererComboBoxItem
 * @property {*} value Item value.
 * @property {string|*|null} [label] Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
 * @property {boolean} [disabled] Disables the item.
 */

/**
 * @typedef {object} RendererComboBoxOptions
 * @property {RendererCss|null} [css] Select CSS.
 * @property {object|null} [props] Select props.
 * @property {function(): *} [onValueGet] Returns the current selected value when it should be pulled lazily.
 * @property {function(*, RendererComboBoxItem=, Event=): void} [onValueSet] Stores the selected value after user interaction.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one item for callback rendering.
 * @property {array<RendererComboBoxItem>} [items] Items rendered when explicit content and callback rendering are not provided.
 * @property {*|null} [emptyValue] Value representing the empty choice.
 * @property {string|*|null} [emptyLabel] Label for the empty choice.
 * @property {boolean} [emptyNotDisplayed] Set to true to suppress rendering of the empty choice.
 */

/**
 * @typedef {object} RendererTableColumn
 * @property {string} key Column data key.
 * @property {string|*|null} [label] Column header label. A string is interpreted as text; any other value is treated as an already rendered label node.
 * @property {boolean} [visible] Set to false to suppress rendering of the column.
 * @property {'left'|'center'|'right'|null} [align] Column alignment.
 * @property {string|number|null} [width] Column width.
 */

/**
 * @typedef {'head'|'body'|'foot'} RendererTableContext
 */

/**
 * Table rendering source precedence: explicit content first, then table callbacks, then columns/items data rendering.
 * @typedef {object} RendererTableOptions
 * @property {RendererCss|null} [css] Table CSS.
 * @property {object|null} [props] Table props.
 * @property {array<RendererTableColumn>} [columns] Raw column descriptors.
 * @property {array<object>} [items] Raw body row data.
 * @property {function(RendererTableContext): number} [onColCount] Returns column count for the requested table section.
 * @property {function(RendererTableContext): number} [onRowCount] Returns row count for the requested table section.
 * @property {function(RendererContract, RendererTableContext, number, object|null): *} [onRowRender] Renders one full row.
 * @property {function(RendererContract, RendererTableContext, number, number, object|null, RendererTableColumn|null): *} [onCellRender] Renders one table cell.
 * @property {function(RendererContract): *} [onEmptyContentRender] Renders empty body content when the body has no rows.
 */

/**
 * @typedef {object} RendererEmptyOptions
 * @property {boolean} [visible] Set to false to suppress rendering.
 * @property {'error'|'warning'|'success'|'info'|'default'} [type] Semantic empty-state type.
 * @property {*|string|null} [icon] Icon name or already rendered icon node.
 * @property {string|null} [title] Primary empty-state text.
 * @property {string|null} [subtitle] Secondary empty-state text.
 * @property {RendererCss|null} [css] Additional CSS for the empty state.
 * @property {object|null} [props] Empty-state props.
 */

/**
 * @typedef {object} RendererPaginatorValue
 * @property {number} recordCount Total number of records.
 * @property {number} limit Page size.
 * @property {number} offset Zero-based index of the first record on the current page.
 * @property {number} pageIndex Zero-based current page index.
 * @property {number} pageCount Total number of pages.
 */

/**
 * @typedef {object} RendererPaginatorOptions
 * @property {RendererCss|null} [css] Paginator wrapper CSS.
 * @property {object|null} [props] Paginator wrapper props.
 * @property {array<number>} [pageLimits] Selectable page-size values.
 * @property {boolean} [showFirst] Set to false to hide the first-page action.
 * @property {boolean} [showPrevious] Set to false to hide the previous-page action.
 * @property {boolean} [showNext] Set to false to hide the next-page action.
 * @property {boolean} [showLast] Set to false to hide the last-page action.
 * @property {boolean} [showLimit] Set to false to hide the page-size selector.
 * @property {boolean} [showStatus] Set to false to hide the page/range status.
 * @property {object|null} [labels] Optional action/status label overrides.
 * @property {object|null} [icons] Optional action icon overrides.
 * @property {boolean} [disabled] Disables paginator actions.
 * @property {function(RendererPaginatorValue, Event=, object=): void} [onValueSet] Handles normalized page/limit changes.
 */

/**
 * @typedef {object} RendererUploadButtonOptions
 * @property {RendererCss|null} [css] Button CSS.
 * @property {object|null} [props] Button props.
 * @property {string|null} [accept] Accepted file types.
 * @property {boolean} [multiple] Allows multiple files.
 * @property {string|null} [name] Input name assigned to the transient file input.
 * @property {boolean} [disabled] Disables the visible button.
 * @property {boolean} [resetAfterChange] Clears the transient input value after selection when supported.
 * @property {function(array<File>, Event=, object=): void} [onValueSet] Handles selected files.
 */

/**
 * @typedef {object} RendererDropdownButtonOptions
 * @property {RendererCss|null} [css] Dropdown wrapper CSS.
 * @property {RendererCss|null} [buttonCss] Trigger button CSS.
 * @property {RendererCss|null} [menuCss] Dropdown menu CSS.
 * @property {string|null} [anchor] Dropdown menu anchor, e.g. "bottom right", "bottom left", "top right", or "top left".
 * @property {object|null} [props] Trigger button props.
 * @property {boolean} [expanded] Current expanded state.
 * @property {boolean} [disabled] Disables the trigger.
 * @property {boolean} [autoclose] Closes the dropdown after item interaction when supported.
 * @property {boolean} [closeOnItemClick] Alias-style option for autoclose semantics.
 * @property {function(): number} [onItemCount] Returns item count for callback rendering.
 * @property {function(RendererContract, number): *} [onItemRender] Renders one dropdown item for callback rendering.
 * @property {array<RendererNavigationItemOptions>|null} [items] Menu items rendered when explicit content is not provided.
 * @property {function(boolean, Event=, object=): void} [onValueSet] Handles expanded-state changes.
 * @property {function(object=): void} [onOpen] Called when opening is requested.
 * @property {function(object=): void} [onClose] Called when closing is requested.
 */

export class RendererContract {

    constructor(options = {}) {

        // Contract base class. Concrete renderers are expected to provide at least el(),
        // createView(), injectView(), and renderer-specific UI/component helpers.
        this.iconRenderer = options?.iconRenderer ?? null;

    }

    getElementRenderer() { throw this._notImplemented('getElementRenderer()'); }

    getIconRenderer() { throw this._notImplemented('getIconRenderer()'); }

    /**
     * Strict rendering primitive. Unlike DOM aliases, this method does not support _args() overloads.
     * @param {string} tag HTML tag or renderer-specific selector tag.
     * @param {RendererCss|null} css Renderer-specific CSS class list.
     * @param {object|null} props Element props/attributes/events.
     * @param {*|array|null} content Element content.
     * @returns {*}
     */
    el(tag, css = null, props = null, content = null) { throw this._notImplemented('el(tag, css, props, content)'); }

    /**
     * Renders an icon using the configured icon renderer.
     * String second argument is a convenience overload for { css: cssOrOptions }.
     * @param {string|null} name Icon name or alias.
     * @param {RendererCss|RendererIconOptions|null} cssOrOptions Icon CSS or icon rendering options.
     * @returns {*}
     */
    icon(name = null, cssOrOptions = null) {

        if (!this.iconRenderer || typeof this.iconRenderer.renderIcon !== 'function') {
            throw new Error(`[${this.constructor.name}] iconRenderer is required.`);
        }

        return this.iconRenderer.renderIcon(name, this._iconArgs(cssOrOptions));

    }

    createView(renderFactory, data = null) { throw this._notImplemented('createView(renderFactory, data)'); }

    injectView(view, data = null) { throw this._notImplemented('injectView(view, data)'); }

    // ========================================================================================
    // DOM aliases
    // ========================================================================================

    /**
     * Generic DOM alias. Argument convention matches higher-level renderer helpers:
     * string => { css: string }, plain object => renderer options, array => content.
     * Text content must be wrapped in an array, e.g. span(['text']).
     * Legacy css/props/content triples are intentionally not supported.
     * @param {string} tagName HTML tag name.
     * @param {RendererCss|RendererLayoutOptions|array|null} optionsOrCss DOM alias options, CSS, or content.
     * @param {*|array|null} content DOM alias content.
     * @returns {*}
     */
    tag(tagName, optionsOrCss = null, content = null, ...legacyArgs) {
        const { css, props, content: normalizedContent } = this._domAliasArgs(tagName, optionsOrCss, content, legacyArgs);
        return this.el(tagName, css, props, normalizedContent);
    }

    /** @see tag */
    div(...args) { return this.tag('div', ...args); }

    /** @see tag */
    span(...args) { return this.tag('span', ...args); }

    /** @see tag */
    p(...args) { return this.tag('p', ...args); }

    /** @see tag */
    em(...args) { return this.tag('em', ...args); }

    /** @see tag */
    strong(...args) { return this.tag('strong', ...args); }

    /** Semantic alias for em(). */
    italic(...args) { return this.em(...args); }

    /** Semantic alias for strong(). */
    bold(...args) { return this.strong(...args); }

    /** @see tag */
    sup(...args) { return this.tag('sup', ...args); }

    /** @see tag */
    sub(...args) { return this.tag('sub', ...args); }

    /** @see tag */
    small(...args) { return this.tag('small', ...args); }

    /** @see tag */
    mark(...args) { return this.tag('mark', ...args); }

    /** @see tag */
    code(...args) { return this.tag('code', ...args); }

    /** @see tag */
    pre(...args) { return this.tag('pre', ...args); }

    /** @see tag */
    blockquote(...args) { return this.tag('blockquote', ...args); }

    /** Plain <label> DOM alias. Field labels are handled separately by field(). */
    label(...args) { return this.tag('label', ...args); }

    /** Plain <a> DOM alias. Higher-level URL helper is link(). */
    a(...args) { return this.tag('a', ...args); }

    /** @see tag */
    br(...args) { return this.tag('br', ...args); }

    /** @see tag */
    hr(...args) { return this.tag('hr', ...args); }

    /** Plain <input> DOM alias. Value-aware text editing helper is textBox(). */
    input(...args) { return this.tag('input', ...args); }

    /** Plain <textarea> DOM alias. Value-aware multiline text editing helper is textBox(..., { multiline: true }). */
    textarea(...args) { return this.tag('textarea', ...args); }

    /** Plain <select> DOM alias. Value-aware select helper is comboBox(). */
    select(...args) { return this.tag('select', ...args); }

    /** Plain <option> DOM alias. Combo-box items are handled separately by comboBox(). */
    option(...args) { return this.tag('option', ...args); }

    /** @see tag */
    img(...args) { return this.tag('img', ...args); }

    /** Plain unordered-list DOM alias. UI list collection helper is list(). */
    ul(...args) { return this.tag('ul', ...args); }

    /** Plain ordered-list DOM alias. UI list collection helper is list(). */
    ol(...args) { return this.tag('ol', ...args); }

    /** Plain list-item DOM alias. UI list item helper is listItem(). */
    li(...args) { return this.tag('li', ...args); }

    /** Plain <h1> DOM alias. Higher-level heading helper is heading(). */
    h1(...args) { return this.tag('h1', ...args); }

    /** Plain <h2> DOM alias. Higher-level heading helper is heading(). */
    h2(...args) { return this.tag('h2', ...args); }

    /** Plain <h3> DOM alias. Higher-level heading helper is heading(). */
    h3(...args) { return this.tag('h3', ...args); }

    /** Plain <h4> DOM alias. Higher-level heading helper is heading(). */
    h4(...args) { return this.tag('h4', ...args); }

    /** Plain <h5> DOM alias. Higher-level heading helper is heading(). */
    h5(...args) { return this.tag('h5', ...args); }

    /** Plain <h6> DOM alias. Higher-level heading helper is heading(). */
    h6(...args) { return this.tag('h6', ...args); }

    /** Plain <thead> DOM alias. */
    thead(...args) { return this.tag('thead', ...args); }

    /** Plain <tbody> DOM alias. */
    tbody(...args) { return this.tag('tbody', ...args); }

    /** Plain <tfoot> DOM alias. */
    tfoot(...args) { return this.tag('tfoot', ...args); }

    /** Plain <tr> DOM alias. */
    trow(...args) { return this.tag('tr', ...args); }

    /** Plain <td> DOM alias. */
    tcell(...args) { return this.tag('td', ...args); }

    /** Plain <th> DOM alias. */
    thcell(...args) { return this.tag('th', ...args); }

    // ========================================================================================
    // UI/component helpers - renderer-specific contract
    // ========================================================================================

    /**
     * Higher-level URL link helper. Use a() for a plain DOM anchor.
     * @param {RendererLinkOptions|null} options Link rendering options.
     * @param {*|array|null} content Link content.
     * @returns {*}
     */
    link(options = null, content = null) { throw this._notImplemented('link(options, content)'); }

    /**
     * Applies one or more named text styles to a string or a single already-rendered text VNode.
     * Style may be a style name, an array of style names, or a whitespace-separated style name string.
     * This helper is not a general multi-element content wrapper.
     * @param {RendererTextStyleName|string|array<RendererTextStyleName|string>} style Named style or styles.
     * @param {string|*|null} text Text string or a single already-rendered text VNode.
     * @param {RendererTextStyleOptions|null} options Text style options.
     * @returns {*}
     */
    textStyle(style, text, options = null) { throw this._notImplemented('textStyle(style, text, options)'); }

    /**
     * Heading helper. Level must be an integer from 1 to 6 and maps to the matching HTML heading tag.
     * The text argument may be a string or an already-rendered VNode.
     * @param {number} level Heading level, from 1 to 6.
     * @param {string|*|null} text Heading text/content.
     * @param {RendererHeadingOptions|null} options Heading options.
     * @returns {*}
     */
    heading(level, text, options = null) { throw this._notImplemented('heading(level, text, options)'); }

    /**
     * Application root layout helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    app(options = null, content = null) { throw this._notImplemented('app(options, content)'); }

    /**
     * Main content layout helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    main(options = null, content = null) { throw this._notImplemented('main(options, content)'); }

    /**
     * Active application module layout helper. Represents the application's current main content area.
     * In an application shell, only the active rendered module should be represented as module().
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    module(options = null, content = null) { throw this._notImplemented('module(options, content)'); }

    /**
     * Layout header helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    header(options = null, content = null) { throw this._notImplemented('header(options, content)'); }

    /**
     * Layout footer helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    footer(options = null, content = null) { throw this._notImplemented('footer(options, content)'); }

    /**
     * Layout section helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    section(options = null, content = null) { throw this._notImplemented('section(options, content)'); }

    /**
     * Layout aside helper. The position option is a layout hint for side placement.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    aside(options = null, content = null) { throw this._notImplemented('aside(options, content)'); }

    /**
     * Self-contained content card helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    card(options = null, content = null) { throw this._notImplemented('card(options, content)'); }

    /**
     * Horizontal layout row helper without additional HTML semantics.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    row(options = null, content = null) { throw this._notImplemented('row(options, content)'); }

    /**
     * Dialog layout helper. Concrete renderers may render an overlay/wrapper as needed.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    dialog(options = null, content = null) { throw this._notImplemented('dialog(options, content)'); }

    /**
     * Visual group layout helper, typically used to connect related controls such as buttons.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Layout content.
     * @returns {*}
     */
    group(options = null, content = null) { throw this._notImplemented('group(options, content)'); }

    /**
     * Grid layout helper. Concrete renderers may implement this as a 12-column responsive grid.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Grid cells/content.
     * @returns {*}
     */
    grid(options = null, content = null) { throw this._notImplemented('grid(options, content)'); }

    /**
     * Grid cell layout helper.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Cell content.
     * @returns {*}
     */
    cell(options = null, content = null) { throw this._notImplemented('cell(options, content)'); }

    /**
     * Stack layout helper for one-dimensional vertical or horizontal layouts.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @param {*|array|null} content Stack content.
     * @returns {*}
     */
    stack(options = null, content = null) { throw this._notImplemented('stack(options, content)'); }

    /**
     * Spacer layout helper for inserting empty flexible or fixed space.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @returns {*}
     */
    spacer(options = null) { throw this._notImplemented('spacer(options)'); }

    /**
     * Divider layout helper for separating adjacent content.
     * @param {RendererLayoutOptions|null} options Layout options.
     * @returns {*}
     */
    divider(options = null) { throw this._notImplemented('divider(options)'); }

    /**
     * UI list collection helper. Rendering source precedence: content, then callbacks, then options.items.
     * When options.items is used, concrete renderers should render each item through listItem().
     * @param {RendererCollectionOptions|null} options Collection rendering options.
     * @param {array|null} content Already rendered list items. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    list(options = null, content = null) { throw this._notImplemented('list(options, content)'); }

    /**
     * UI list item helper.
     * @param {RendererCollectionItem|null} options Item rendering options.
     * @param {*|array|null} content Item content.
     * @returns {*}
     */
    listItem(options = null, content = null) { throw this._notImplemented('listItem(options, content)'); }

    /**
     * UI navigation collection helper. Rendering source precedence: content, then callbacks, then raw options.items.
     * @param {RendererNavOptions|null} options Navigation rendering options.
     * @param {array|null} content Already rendered nav items. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    nav(options = null, content = null) { throw this._notImplemented('nav(options, content)'); }

    /**
     * UI navigation item helper. Raw child item data belongs to options.items; already rendered child navigation belongs to subNav.
     * The content argument is a complete override of the item's default inner label/icon rendering.
     * @param {string|*|null} label Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
     * @param {string|*|null} icon Optional icon. A string is interpreted as an icon name; any other value is treated as an already rendered icon node.
     * @param {RendererNavigationItemOptions|null} options Navigation item options.
     * @param {*|array|null} subNav Already rendered child navigation/menu content.
     * @param {*|array|null} content Explicit override for the item's default inner content.
     * @returns {*}
     */
    navItem(label = null, icon = null, options = null, subNav = null, content = null) { throw this._notImplemented('navItem(label, icon, options, subNav, content)'); }

    /**
     * UI menu collection helper. Rendering source precedence: content, then callbacks, then raw options.items.
     * @param {RendererMenuOptions|null} options Menu rendering options.
     * @param {array|null} content Already rendered menu items. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    menu(options = null, content = null) { throw this._notImplemented('menu(options, content)'); }

    /**
     * UI menu item helper. Raw child item data belongs to options.items; already rendered child menu belongs to subMenu.
     * The content argument is a complete override of the item's default inner label/icon rendering.
     * @param {string|*|null} label Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
     * @param {string|*|null} icon Optional icon. A string is interpreted as an icon name; any other value is treated as an already rendered icon node.
     * @param {RendererNavigationItemOptions|null} options Menu item options.
     * @param {*|array|null} subMenu Already rendered child menu/navigation content.
     * @param {*|array|null} content Explicit override for the item's default inner content.
     * @returns {*}
     */
    menuItem(label = null, icon = null, options = null, subMenu = null, content = null) { throw this._notImplemented('menuItem(label, icon, options, subMenu, content)'); }

    /**
     * UI breadcrumbs navigation helper. Rendering source precedence: content, then callbacks, then raw options.items.
     * When options.items is used, concrete renderers should render each item through breadcrumbItem().
     * @param {RendererBreadcrumbsOptions|null} options Breadcrumbs rendering options.
     * @param {array|null} content Already rendered breadcrumb items. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    breadcrumbs(options = null, content = null) { throw this._notImplemented('breadcrumbs(options, content)'); }

    /**
     * UI breadcrumb item helper. Follows navigation/menu item conventions, without nested child navigation.
     * The content argument is a complete override of the item's default inner label/icon rendering.
     * @param {string|*|null} label Item label. A string is interpreted as text; any other value is treated as an already rendered label node.
     * @param {string|*|null} icon Optional icon. A string is interpreted as an icon name; any other value is treated as an already rendered icon node.
     * @param {RendererNavigationItemOptions|null} options Breadcrumb item options.
     * @param {*|array|null} content Explicit override for the item's default inner content.
     * @returns {*}
     */
    breadcrumbItem(label = null, icon = null, options = null, content = null) { throw this._notImplemented('breadcrumbItem(label, icon, options, content)'); }

    /**
     * Table helper. Header/body/footer cell rendering is expected to use table DOM aliases.
     * Rendering source precedence: content, then table callbacks, then columns/items data rendering.
     * Table contexts are 'head', 'body', and 'foot'. Row item is resolved from options.items for body rows;
     * for head and foot rows it is null unless a concrete renderer provides another source.
     * @param {RendererTableOptions|null} options Table rendering options.
     * @param {array|null} content Already rendered table content. When provided, callbacks and columns/items rendering are ignored.
     * @returns {*}
     */
    table(options = null, content = null) { throw this._notImplemented('table(options, content)'); }

    /**
     * General button helper.
     * @param {string|*|null} label Button label. A string is interpreted as text; any other value is treated as an already rendered label node.
     * @param {string|*|null} icon Optional icon. A string is interpreted as an icon name; any other value is treated as an already rendered icon node.
     * @param {RendererButtonOptions|null} options Button rendering options.
     * @param {array|null} content Explicit button content.
     * @returns {*}
     */
    button(label = null, icon = null, options = null, content = null) { throw this._notImplemented('button(label, icon, options, content)'); }

    /**
     * Text-only button alias for button(text, null, options, content).
     * @param {string|*|null} text Button label. A string is interpreted as text; any other value is treated as an already rendered label node.
     * @param {RendererButtonOptions|null} options Button rendering options.
     * @param {array|null} content Explicit button content.
     * @returns {*}
     */
    textButton(text = null, options = null, content = null) { return this.button(text, null, options, content); }

    /**
     * Icon-only button alias for button(null, icon, options, content).
     * @param {string|*|null} icon Optional icon. A string is interpreted as an icon name; any other value is treated as an already rendered icon node.
     * @param {RendererButtonOptions|null} options Button rendering options.
     * @param {array|null} content Explicit button content.
     * @returns {*}
     */
    iconButton(icon = null, options = null, content = null) { return this.button(null, icon, options, content); }

    /**
     * Composite paginator helper.
     * @param {number} recordCount Total number of records.
     * @param {number} limit Page size.
     * @param {number} offset Zero-based index of the first record on the current page.
     * @param {RendererPaginatorOptions|null} options Paginator options.
     * @returns {*}
     */
    paginator(recordCount = 0, limit = 25, offset = 0, options = null) { throw this._notImplemented('paginator(recordCount, limit, offset, options)'); }

    /**
     * Button-like file picker helper. The file input is renderer-owned and not part of the rendered content tree.
     * @param {string|*|null} label Button label.
     * @param {string|*|null} icon Optional icon.
     * @param {RendererUploadButtonOptions|null} options Upload button options.
     * @param {array|null} content Explicit visible button content.
     * @returns {*}
     */
    uploadButton(label = null, icon = null, options = null, content = null) { throw this._notImplemented('uploadButton(label, icon, options, content)'); }

    /**
     * Button-like dropdown menu helper. Explicit content is the dropdown body; otherwise options.items/callbacks are used.
     * @param {string|*|null} label Button label.
     * @param {string|*|null} icon Optional icon.
     * @param {RendererDropdownButtonOptions|null} options Dropdown button options.
     * @param {*|array|null} content Explicit dropdown content.
     * @returns {*}
     */
    dropdownButton(label = null, icon = null, options = null, content = null) { throw this._notImplemented('dropdownButton(label, icon, options, content)'); }

    /**
     * Form field helper. Concrete renderer owns label/hint/error markup. Content is the already-rendered value editor/control.
     * @param {RendererFieldOptions|null} options Field rendering options.
     * @param {*|array|null} content Rendered field control/editor content.
     * @returns {*}
     */
    field(options = null, content = null) { throw this._notImplemented('field(options, content)'); }

    /**
     * Value-aware text editor helper. Use input() or textarea() for dumb DOM rendering.
     * @param {*|null} value Source value. Concrete renderers may override it with options.onValueGet().
     * @param {RendererTextBoxOptions|null} options Value formatting/parsing options.
     * @returns {*}
     */
    textBox(value = null, options = null) { throw this._notImplemented('textBox(value, options)'); }

    /**
     * Value-aware slider helper.
     * @param {number|null} value Slider value. Concrete renderers may override it with options.onValueGet().
     * @param {RendererSliderOptions|null} options Slider rendering/value options.
     * @returns {*}
     */
    slider(value = null, options = null) { throw this._notImplemented('slider(value, options)'); }

    /**
     * Value display progress helper.
     * @param {number|null} value Progress value. Concrete renderers may override it with options.onValueGet().
     * @param {RendererProgressOptions|null} options Progress rendering options.
     * @returns {*}
     */
    progress(value = null, options = null) { throw this._notImplemented('progress(value, options)'); }

    /**
     * Value-aware checkbox helper. Radio inputs are intentionally represented only through radioGroup().
     * @param {boolean} value Checkbox value. Concrete renderers may override it with options.onValueGet().
     * @param {string|*|null} label Checkbox label. A string is interpreted as text; any other value is treated as an already rendered label node.
     * @param {RendererCheckBoxOptions|null} options Value get/set options.
     * @param {*|array|null} content Explicit override for checkbox label/control content.
     * @returns {*}
     */
    checkBox(value = false, label = null, options = null, content = null) { throw this._notImplemented('checkBox(value, label, options, content)'); }

    /**
     * Value-aware checkbox group helper. The group value is an array of selected item values.
     * Rendering source precedence: content, then callbacks, then options.items.
     * @param {array<*>} value Selected values. Concrete renderers may override it with options.onValueGet().
     * @param {RendererCheckGroupOptions|null} options Group rendering options.
     * @param {array|null} content Explicit checkbox item content. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    checkGroup(value = [], options = null, content = null) { throw this._notImplemented('checkGroup(value, options, content)'); }

    /**
     * Value-aware radio group helper. Individual radio inputs are represented by this group helper,
     * not by a standalone radioBox(). Rendering source precedence: content, then callbacks,
     * then options.items.
     * @param {string} name Radio input group name.
     * @param {*|null} value Selected value. Concrete renderers may override it with options.onValueGet().
     * @param {RendererRadioGroupOptions|null} options Group rendering options.
     * @param {array|null} content Explicit radio item content. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    radioGroup(name, value = null, options = null, content = null) { throw this._notImplemented('radioGroup(name, value, options, content)'); }

    /**
     * Value-aware combo-box/select helper. Use select() and option() for dumb DOM rendering.
     * Rendering source precedence: content, then callbacks, then options.items.
     * @param {*|null} value Selected value. Concrete renderers may override it with options.onValueGet().
     * @param {RendererComboBoxOptions|null} options Combo-box rendering options.
     * @param {array|null} content Explicit option content. When provided, options.items/callback rendering is ignored.
     * @returns {*}
     */
    comboBox(value = null, options = null, content = null) { throw this._notImplemented('comboBox(value, options, content)'); }

    /**
     * Empty-state helper. Explicit content is a complete override of empty-state content derived from options.
     * @param {RendererEmptyOptions|null} options Empty-state options.
     * @param {*|array|null} content Explicit empty-state content.
     * @returns {*}
     */
    empty(options = null, content = null) { throw this._notImplemented('empty(options, content)'); }

    // ========================================================================================
    // Protected helpers for concrete renderers
    // ========================================================================================

    /**
     * Shared collection-content resolver for helpers that support manual, callback, and items rendering.
     * Rendering source precedence: content, then callbacks, then options.items.
     * @param {string} itemHelperName Name of helper used for default item wrapping.
     * @param {array|null} content Explicit content. Returned as-is when provided.
     * @param {RendererCollectionOptions|null} options Collection rendering options.
     * @returns {array|null}
     */
    _renderCollectionContent(itemHelperName, content = null, options = null) {

        if (content !== null && content !== undefined) return content;

        if (typeof options?.onItemCount === 'function' && typeof options?.onItemRender === 'function') {
            const count = options.onItemCount();
            return Array.from({ length: count }, (_, index) => options.onItemRender(this, index));
        }

        if (Array.isArray(options?.items)) {
            return options.items.map((item) => this[itemHelperName](item, item?.content ?? [item?.label ?? item]));
        }

        return null;

    }

    /**
     * Parses DOM alias args. Supported convention: string => css, plain object => renderer options,
     * array => content. Legacy css/props/content triples are intentionally not supported.
     * @param {string} tagName HTML tag name for diagnostics.
     * @param {RendererCss|object|array|null} optionsOrCss DOM alias options, CSS, or content.
     * @param {*|array|null} content DOM alias content.
     * @param {array} legacyArgs Extra legacy args passed to fail fast.
     * @returns {{ css: string|null, props: object|null, content: *|array|null }}
     */
    _domAliasArgs(tagName, optionsOrCss = null, content = null, legacyArgs = []) {

        if (legacyArgs.length) {
            throw new Error(`[${this.constructor.name}] ${tagName}() DOM alias supports only (content), (css, content), or (options, content). Legacy css/props/content triples are not supported.`);
        }

        if (optionsOrCss === null || optionsOrCss === undefined) {
            return { css: null, props: null, content };
        }

        if (typeof optionsOrCss === 'string') {
            return { css: optionsOrCss, props: null, content };
        }

        if (Array.isArray(optionsOrCss)) {
            if (content !== null && content !== undefined)
                throw new Error(`[${this.constructor.name}] ${tagName}() received duplicate content.`);
            return { css: null, props: null, content: optionsOrCss };
        }

        if (this._isPlainObject(optionsOrCss)) {
            this._assertDomAliasOptions(tagName, optionsOrCss);
            return {
                css: optionsOrCss.css ?? null,
                props: optionsOrCss.props ?? null,
                content: content ?? optionsOrCss.content ?? null,
            };
        }

        throw new Error(`[${this.constructor.name}] ${tagName}() unsupported DOM alias options type: ${typeof optionsOrCss}.`);

    }

    _assertDomAliasOptions(tagName, options) {

        const allowedKeys = new Set(['css', 'props', 'content']);
        const invalidKeys = Object.keys(options).filter((key) => !allowedKeys.has(key));

        if (invalidKeys.length) {
            throw new Error(`[${this.constructor.name}] ${tagName}() DOM alias object argument is renderer options. Put DOM attributes under { props: { ${invalidKeys.join(', ')} } }.`);
        }

    }

    _iconArgs(cssOrOptions = null) {

        if (cssOrOptions === null || cssOrOptions === undefined) return null;
        if (typeof cssOrOptions === 'string') return { css: cssOrOptions };
        if (this._isPlainObject(cssOrOptions)) return cssOrOptions;

        throw new Error(`[${this.constructor.name}] icon() unsupported icon options type: ${typeof cssOrOptions}.`);

    }

    _isPlainObject(value) {
        return value !== null && typeof value === 'object' && value.constructor === Object;
    }

    /**
     * Normalizes RendererCss input to the preferred space-separated class-name format.
     * @param {RendererCss|null} css Renderer CSS class list.
     * @returns {RendererCss|null}
     */
    _normalizeCss(css = null) {

        if (css === null || css === undefined) return null;
        if (typeof css !== 'string') return css;

        const trimmed = css.trim();
        if (trimmed === '') return null;

        if (trimmed.startsWith('.') && !/\s/.test(trimmed)) {
            return trimmed.slice(1).split('.').filter(Boolean).join(' ');
        }

        return trimmed;

    }

    _notImplemented(signature) {
        return new Error(`[${this.constructor.name}] ${signature} is not implemented.`);
    }

}
